import { arrangePalette, focusRevealed, paletteAddAfter, pinPanelTop, showJustAdded } from './arrange-edit';
import { DROP_CANCEL_EVENT } from './site-analytics-arrange';

/**
 * The Site Analytics page's edit mode in the browser (livewire/sites/analytics.blade.php, on the
 * page's root): each arrange action's answer, the Add chart panel, and nothing else. The grid's
 * drag and resize are site-analytics-arrange.js's, the gap targets report-grid-gaps.js's, and the
 * rules shared with a report's edit mode (how focus reaches a control in a closed disclosure, how
 * the panel searches, counts and gets out of a drag's way) arrange-edit.js's.
 *
 * Every arrange action on Sites\Analytics answers in one `site-analytics-arrange` window event
 * (Analytics::arrangeEvent()): `message`, the sentence for the page's arrange status region, its own
 * polite region, so a load a change sets off never talks over it; and `focus`, where focus goes once
 * the answer has landed, found in the page by data attribute, never by a selector built from an id:
 *
 *  - `start`: the header's Arrange (after Save and Discard);
 *  - `palette-trigger`: the header's Add chart, the panel's trigger;
 *  - `palette`: the panel's search; `palette-entry`: the Add of the entry `key` names, or the
 *    nearest one that can take focus (paletteAddAfter()), with the card just added (`cardId`)
 *    outlined for a few seconds;
 *  - `add-tile`: the end tile after the grid, else Add chart;
 *  - `card`: a card's name in its edit toolbar while arranging, else its heading;
 *  - `control`: a control of a card's edit toolbar (`control`), else the other move button at an
 *    end, else the card's name;
 *  - `undo` and `reset`: the edit bar's Undo and Reset to default.
 *
 * The config is fixed for the page's life (Livewire keeps an Alpine root's state across morphs and
 * never reads its expression again): the words the panel's count and its "Just added" label say.
 *
 * @param {{ text?: Record<string, string> }} config
 */
export default function SiteAnalyticsEditMode(config = {}) {
    const text = config.text ?? {};

    /** Where a card's control hands focus when it has just become disabled at an end. */
    const CONTROL_FALLBACK = { 'move-up': 'move-down', 'move-down': 'move-up' };

    /** The controls a focus instruction may name in a card's edit toolbar. */
    const CONTROLS = new Set(['move-up', 'move-down', 'size', 'move', 'remove']);

    let onDropCancel = null;

    return {
        ...arrangePalette({
            search: '[data-site-analytics-palette-search]',
            panel: '[data-site-analytics-palette]',
            sections: { questions: true, figures: true, charts: true, tables: true },
            text,
            announce(message) {
                this.sae_announce(message);
            },
        }),

        /** What the arrange status region says: set a frame after it is cleared, so the same sentence twice is said twice. */
        sae_status: '',

        init() {
            onDropCancel = () => this.pal_dropCancelled();
            window.addEventListener(DROP_CANCEL_EVENT, onDropCancel);
        },

        destroy() {
            if (onDropCancel !== null) window.removeEventListener(DROP_CANCEL_EVENT, onDropCancel);

            onDropCancel = null;
        },

        /** One arrange action's answer (see the file's docblock). */
        sae_arrange(detail) {
            if (! detail || typeof detail !== 'object') return;

            // Whatever the action was, it has answered: a panel still open after it (an add the
            // server refused) shows every entry again rather than the one that was pressed.
            this.pal_holding = false;

            if (typeof detail.message === 'string' && detail.message !== '') {
                this.pal_quiet();
                this.sae_announce(detail.message);
            }

            if (detail.focus && typeof detail.focus === 'object') {
                requestAnimationFrame(() => focusRevealed(this.sae_focusTarget(detail.focus)));
            }
        },

        sae_announce(message) {
            this.sae_status = '';
            requestAnimationFrame(() => {
                this.sae_status = message;
            });
        },

        /** The page's element a focus instruction names; see the file's docblock. */
        sae_focusTarget(focus) {
            const find = (selector) => document.querySelector(selector);

            switch (focus.target) {
                case 'start':
                    return find('[data-site-analytics-arrange-start]');
                case 'palette-trigger':
                    return find('[data-site-analytics-palette-trigger]');
                case 'add-tile':
                    return find('[data-site-analytics-add-tile]') ?? find('[data-site-analytics-palette-trigger]');
                case 'palette':
                    return find('[data-site-analytics-palette-search]');
                case 'palette-entry': {
                    this.sae_showAdded(focus.cardId);

                    const entries = [...document.querySelectorAll('[data-site-analytics-palette] [data-palette-key]')];

                    return paletteAddAfter(entries, focus.key) ?? find('[data-site-analytics-palette-search]');
                }
                case 'undo':
                    return find('[data-site-analytics-undo]');
                case 'reset':
                    return find('[data-site-analytics-arrange-reset]');
                case 'card':
                case 'control': {
                    const card = this.sae_card(focus.cardId);
                    const title = card?.querySelector?.('[data-arrange-title]') ?? card?.querySelector?.('[data-card-heading]') ?? null;

                    if (focus.target === 'card' || ! CONTROLS.has(focus.control)) return title;

                    const controls = [...(card?.querySelectorAll?.('[data-arrange-control]') ?? [])];
                    const control = (name) => controls.find((element) => element.getAttribute('data-arrange-control') === name && ! element.disabled) ?? null;

                    return control(focus.control)
                        ?? (CONTROL_FALLBACK[focus.control] ? control(CONTROL_FALLBACK[focus.control]) : null)
                        ?? title;
                }
                default:
                    return null;
            }
        },

        /** A card's element, matched by attribute value: an id is stored free text. */
        sae_card(cardId) {
            if (typeof cardId !== 'string' || cardId === '') return null;

            return [...document.querySelectorAll('[data-site-analytics-card]')]
                .find((element) => element.getAttribute('data-site-analytics-card') === cardId) ?? null;
        },

        /** The card an add from the open panel made, outlined and, beside the fixed panel, scrolled into view. */
        sae_showAdded(cardId) {
            showJustAdded(this.sae_card(cardId), document.querySelector('[data-site-analytics-palette]'), text.paletteJustAdded);
        },

        /** The fixed panel's top, kept under the app's top bar and the Insights top bar (lg up). */
        sae_pinPanel(el) {
            pinPanelTop(el, '--site-analytics-palette-top', '--insights-header-height');
        },
    };
}
